import { customDisciplineLabel } from "../../../util/src/custom-disciplines/labels.js";
import { useCompiledLayoutRuntimeData } from "./CompiledLayoutRuntimeContext.js";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { frFR, enUS } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { getLanguage } from "../../../util/src/language/dico.js";
import React from "react";
import type { RenderCustomDisciplineInputNode as RenderCustomDisciplineInputNodeModel } from "../../../util/src/layout-compiler/types.js";
import { useOnCustomDisciplineInputChange } from "./CompiledLayoutRuntimeContext.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";

type RenderCustomDisciplineInputNodeProps = {
  node: RenderCustomDisciplineInputNodeModel;
  scale: number;
};

let nextInputLabelId = 0;

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) return `${rotationTransform} ${extra}`;
  return rotationTransform ?? extra;
}

/**
 * Renders a `customDisciplineInput` component node (custom discipline modal
 * layouts). This is a *controlled* field: its value comes from `node.value`
 * (resolved at compile-time from `data.inputValues[fieldKey]`), and edits are
 * reported to the host page via `CompiledLayoutRenderer`'s
 * `onCustomDisciplineInputChange` prop, which owns the `inputValues` state.
 */
export function RenderCustomDisciplineInputNode({
  node,
  scale,
}: RenderCustomDisciplineInputNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const [inputLabelId] = React.useState(
    () => `custom-input-label-${++nextInputLabelId}`,
  );
  const onCustomDisciplineInputChange = useOnCustomDisciplineInputChange();
  const clipStyle = getRenderNodeClipStyle(node, scale);
  const { props, value, options } = node;
  const fieldKey = String(props.fieldKey ?? props.key ?? "").trim();
  const labels = useCompiledLayoutRuntimeData()?.__customDisciplineLabels;
  const label = customDisciplineLabel(labels, props.label ?? fieldKey);
  const readOnly = props.readOnly === true;

  const emitChange = (nextValue: string) => {
    if (fieldKey) {
      onCustomDisciplineInputChange?.(fieldKey, nextValue);
      if (
        (props.type === "SELECT" || props.type === "FOREIGN_KEY") &&
        !fieldKey.endsWith("Id")
      ) {
        const selectedOption = options.find(
          (option) => option.value === nextValue,
        );
        onCustomDisciplineInputChange?.(`${fieldKey}Id`, nextValue);
        onCustomDisciplineInputChange?.(
          `${fieldKey}Label`,
          selectedOption?.label ?? "",
        );
      }
    }
  };

  const wrapperSx = {
    ...(node.style.position === "relative"
      ? {
          position: "relative" as const,
          flexShrink: 0,
        }
      : {
          position: "absolute" as const,
          left: node.x * scale,
          top: node.y * scale,
        }),
    width: node.width * scale,
    height: node.height * scale,
    zIndex: node.zIndex,
    opacity: node.style.opacity as number | undefined,
    boxSizing: "border-box" as const,
    ...(node.style as React.CSSProperties),
    transform: composeTransform(node.rotation, node.style?.transform),
    transformOrigin: "center center",
    ...clipStyle,
    "& .MuiInputBase-input, & .MuiSelect-select, & .MuiInputLabel-root, & .MuiFormControlLabel-label":
      {
        ...(node.style.color ? { color: String(node.style.color) } : {}),
        ...(node.style.fontSize
          ? { fontSize: Number(node.style.fontSize) * scale }
          : {}),
        ...(node.style.fontFamily
          ? { fontFamily: String(node.style.fontFamily) }
          : {}),
        ...(node.style.fontWeight
          ? { fontWeight: String(node.style.fontWeight) }
          : {}),
        ...(node.style.textAlign
          ? { textAlign: String(node.style.textAlign) }
          : {}),
      },
  };

  if (props.type === "BOOLEAN") {
    return (
      <FormControlLabel
        {...runtimeProps}
        label={label}
        sx={{
          ...wrapperSx,
          m: 0,
          justifyContent:
            node.style.textAlign === "center"
              ? "center"
              : node.style.textAlign === "right"
                ? "flex-end"
                : "flex-start",
        }}
        control={
          <Checkbox
            checked={value === "true"}
            disabled={readOnly}
            inputProps={{ "aria-label": label || props.key || fieldKey }}
            sx={
              node.style.color
                ? { "&.Mui-checked": { color: String(node.style.color) } }
                : undefined
            }
            onChange={(event) =>
              emitChange(event.target.checked ? "true" : "false")
            }
          />
        }
      />
    );
  }

  if (props.type === "DATE") {
    const isFrench = getLanguage().startsWith("fr");
    return (
      <FormControl {...runtimeProps} sx={wrapperSx}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={isFrench ? "fr" : "en"}
          localeText={
            (isFrench ? frFR : enUS).components.MuiLocalizationProvider
              .defaultProps.localeText
          }
        >
          <DatePicker
            label={label}
            disabled={readOnly}
            value={value ? dayjs(value) : null}
            format={isFrench ? "DD/MM/YYYY" : "MM/DD/YYYY"}
            onChange={(date) =>
              emitChange(date?.isValid() ? date.format("YYYY-MM-DD") : "")
            }
            slotProps={{
              textField: {
                size: props.size ?? "small",
                required: props.required === true,
              },
            }}
          />
        </LocalizationProvider>
      </FormControl>
    );
  }

  if (props.type === "SELECT" || props.type === "FOREIGN_KEY") {
    return (
      <FormControl
        {...runtimeProps}
        size={props.size ?? "small"}
        required={props.required === true}
        sx={wrapperSx}
      >
        <InputLabel id={inputLabelId} shrink={props.labelShrink}>
          {label}
        </InputLabel>
        <Select
          labelId={inputLabelId}
          label={label}
          value={value}
          disabled={
            readOnly ||
            (props.disableWhenEmpty === true && options.length === 0)
          }
          notched={props.labelShrink}
          onChange={(event) => emitChange(String(event.target.value))}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {customDisciplineLabel(labels, option.label || option.value)}
            </MenuItem>
          ))}
        </Select>
        {options.length === 0 && props.emptyOptionsText && (
          <Typography variant="body2" color="text.secondary">
            {customDisciplineLabel(labels, props.emptyOptionsText)}
          </Typography>
        )}
      </FormControl>
    );
  }

  return (
    <TextField
      {...runtimeProps}
      size={props.size ?? "small"}
      label={label}
      type={props.type === "NUMBER" ? "number" : "text"}
      value={value}
      required={props.required === true}
      disabled={readOnly}
      onChange={(event) => emitChange(event.target.value)}
      sx={wrapperSx}
    />
  );
}
